/**
 * SEARCH-002 §4.1 — Surlignage, dans une ligne de résultat, de ce que la saisie y a trouvé.
 *
 * La recherche ⌘K lit la saisie MOT À MOT (`global_search`, palier des mots) : une fiche
 * répond quand chaque mot tapé y commence un mot. Le surlignage dit la même chose, sinon
 * rien n'explique une ligne trouvée par « dupont formation » ou « formation dupont » :
 *
 * - **saisie en mots** : chaque mot tapé de 2 lettres et plus, hors mots vides, est
 *   surligné partout où il COMMENCE un mot du texte (« form » dans « Dupont Formation ») ;
 *   jamais en milieu de mot (« der » ne surligne pas « Kinder »). Une faute corrigée par
 *   le serveur (« dupnt » → Dupont) n'est pas surlignée : le texte ne l'écrit pas ;
 * - **saisie d'identifiant** (un bloc avec @, point ou chiffre, ou une saisie avec @) : la
 *   saisie ENTIÈRE est surlignée là où elle apparaît, comme une sous-chaîne.
 *
 * Casse, accents et ligatures ne comptent pas (« muller » surligne « Müller », « oeuvre »
 * surligne « Œuvre ») ; le texte AFFICHÉ garde ses caractères d'origine. Les plages qui se
 * chevauchent sont fusionnées. Aucune expression régulière n'est construite depuis la
 * saisie : les comparaisons passent par `startsWith` / `indexOf`.
 *
 * JAMAIS `dangerouslySetInnerHTML` : la fonction rend des nœuds React.
 */

import type { ReactNode } from 'react'
import { MOTS_VIDES_DE_LA_SAISIE } from './highlightMatch.helpers'

/** Longueur minimale d'un mot tapé pour être surligné (un « b » isolé surlignerait tout). */
const MOT_MIN = 2

/**
 * Lettres que NFKD ne décompose pas, ramenées comme le fait le serveur (`_LIGATURES`) :
 * sans elles, « Œuvre » ne répondrait pas à « oeuvre ».
 */
const LIGATURES: Readonly<Record<string, string>> = {
    œ: 'oe', Œ: 'oe', æ: 'ae', Æ: 'ae', ß: 'ss', ẞ: 'ss',
    ø: 'o', Ø: 'o', ł: 'l', Ł: 'l', ŀ: 'l', Ŀ: 'l', đ: 'd', Đ: 'd',
    ð: 'd', Ð: 'd', þ: 'th', Þ: 'th', ħ: 'h', Ħ: 'h', ı: 'i',
    ĸ: 'q', ŋ: 'n', Ŋ: 'n', ŧ: 't', Ŧ: 't',
}

const DIACRITIQUES = /\p{M}/gu
const SEPARATEURS = /[^a-z0-9]+/

/** Un caractère plié : minuscule, sans accent, ligature dépliée (peut faire 0, 1 ou 2 lettres). */
function plierCaractere(ch: string): string {
    return LIGATURES[ch] ?? ch.normalize('NFKD').replace(DIACRITIQUES, '').toLowerCase()
}

/** Le texte plié, et pour chaque lettre pliée la plage [début, fin) du caractère d'origine. */
interface TextePlie {
    plie: string
    debut: number[]
    fin: number[]
}

function plier(texte: string): TextePlie {
    let plie = ''
    const debut: number[] = []
    const fin: number[] = []
    let i = 0
    for (const ch of texte) {
        const p = plierCaractere(ch)
        // Un accent écrit À PART (« e » + U+0301) suit sa lettre dans la plage : la couper
        // poserait l'accent hors du gras.
        if (!p && fin.length > 0 && fin[fin.length - 1] === i) fin[fin.length - 1] = i + ch.length
        for (const lettre of p) {
            plie += lettre
            debut.push(i)
            fin.push(i + ch.length)
        }
        i += ch.length
    }
    return { plie, debut, fin }
}

function estAlphanumerique(c: string | undefined): boolean {
    return c !== undefined && ((c >= 'a' && c <= 'z') || (c >= '0' && c <= '9'))
}

/** Même lecture que le serveur (`analyser`) : un identifiant ne se lit ni en mots ni en fautes. */
export function estUneSaisieDIdentifiant(saisie: string): boolean {
    const q = saisie.trim()
    return q.includes('@') || (!/\s/.test(q) && /[.0-9]/.test(q))
}

/** Les mots tapés qui se surlignent : pliés, 2 lettres et plus, hors mots vides, sans doublon. */
export function motsASurligner(saisie: string): string[] {
    const mots = plier(saisie).plie.split(SEPARATEURS)
    return [...new Set(mots.filter((m) => m.length >= MOT_MIN && !MOTS_VIDES_DE_LA_SAISIE.has(m)))]
}

type Plage = [number, number]

function plagesDesMots(t: TextePlie, mots: string[]): Plage[] {
    const plages: Plage[] = []
    for (let p = 0; p < t.plie.length; p++) {
        if (!estAlphanumerique(t.plie[p]) || estAlphanumerique(t.plie[p - 1])) continue
        for (const mot of mots) {
            if (t.plie.startsWith(mot, p)) plages.push([t.debut[p], t.fin[p + mot.length - 1]])
        }
    }
    return plages
}

function plagesDeLaSousChaine(t: TextePlie, saisie: string): Plage[] {
    const aiguille = plier(saisie.trim()).plie
    if (!aiguille) return []
    const p = t.plie.indexOf(aiguille)
    return p < 0 ? [] : [[t.debut[p], t.fin[p + aiguille.length - 1]]]
}

function fusionner(plages: Plage[]): Plage[] {
    const triees = [...plages].sort((a, b) => a[0] - b[0] || a[1] - b[1])
    const out: Plage[] = []
    for (const [d, f] of triees) {
        const derniere = out[out.length - 1]
        if (derniere && d <= derniere[1]) derniere[1] = Math.max(derniere[1], f)
        else out.push([d, f])
    }
    return out
}

/** Les plages [début, fin) du texte d'origine à surligner pour cette saisie. */
export function plagesASurligner(texte: string, saisie: string): Plage[] {
    if (!texte || !saisie.trim()) return []
    const t = plier(texte)
    const plages = estUneSaisieDIdentifiant(saisie)
        ? plagesDeLaSousChaine(t, saisie)
        : plagesDesMots(t, motsASurligner(saisie))
    return fusionner(plages)
}

export function highlightMatch(text: string, query: string): ReactNode {
    const plages = plagesASurligner(text, query)
    if (plages.length === 0) return text
    const morceaux: ReactNode[] = []
    let curseur = 0
    for (const [d, f] of plages) {
        if (d > curseur) morceaux.push(text.slice(curseur, d))
        morceaux.push(
            <span key={d} className="font-semibold text-foreground">
                {text.slice(d, f)}
            </span>,
        )
        curseur = f
    }
    if (curseur < text.length) morceaux.push(text.slice(curseur))
    return <>{morceaux}</>
}
